import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { cleaningServices, HOME_LOCATIONS, OFFER_MARKUP } from "../data/services";
import { useLanguage } from "../i18n/LanguageContext";
import { useBackClose } from "../hooks/use-back-close";
import { Reveal } from "./Reveal";

const CleaningCard = ({ service, index, onBook, forceOpen, forceKey }) => {
  const { t, L } = useLanguage();
  const [open, setOpen] = useState(false);
  useBackClose(open, () => setOpen(false));

  useEffect(() => {
    if (forceOpen) setOpen(true);
  }, [forceOpen, forceKey]);

  const items = service.groups
    ? service.groups.flatMap((g) => g.items.map((i) => ({ ...i, group: g.title, groupBn: g.titleBn })))
    : [];

  let flatIdx = -1;

  const bookItem = (it, g) => {
    const useGroup = service.groups.length > 1 && g.title !== service.name;
    const label = useGroup
      ? `${service.name} — ${g.title} — ${it.label}`
      : `${service.name} — ${it.label}`;
    const labelBn = useGroup
      ? `${service.nameBn} — ${g.titleBn || g.title} — ${it.labelBn || it.label}`
      : `${service.nameBn} — ${it.labelBn || it.label}`;
    onBook({
      service: "Cleaning Services",
      category: "Cleaning Services",
      items: [{ label, labelBn, price: it.price, qty: 1 }],
      total: it.price,
      locationOptions: HOME_LOCATIONS,
    });
  };

  return (
    <Reveal delay={index * 0.05}>
      <div
        data-testid={`cleaning-card-${service.id}`}
        className={`rounded-3xl bg-white border shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden hover:shadow-[0_16px_40px_rgb(0,0,0,0.07)] transition-shadow duration-300 ${
          forceOpen && open ? "border-[#457B9D] ring-2 ring-[#457B9D]/30" : "border-stone-200"
        }`}
      >
        <button
          onClick={() => (open ? window.history.back() : setOpen(true))}
          data-testid={`cleaning-toggle-${service.id}`}
          className="w-full flex items-center gap-5 p-5 sm:p-6 text-left"
        >
          <img
            src={service.image}
            alt={service.name}
            loading="lazy"
            className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl object-cover shrink-0"
          />
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-xl sm:text-2xl font-semibold text-stone-900">
              {L(service, "name")}
            </h3>
            <p className="mt-1 text-base text-stone-500 leading-relaxed line-clamp-2">
              {L(service, "detail")}
            </p>
            <p className="mt-2 text-base font-bold text-[#EAB308]" data-testid={`cleaning-price-hint-${service.id}`}>
              {service.groups
                ? `${t.from} ₹${Math.min(...items.map((i) => i.price))}`
                : t.priceOnEnquiry}
            </p>
          </div>
          <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.3 }}>
            <ChevronDown className="w-5 h-5 text-stone-400" />
          </motion.span>
        </button>

        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="overflow-hidden"
            >
              <div className="px-5 sm:px-6 pb-6 border-t border-stone-100">
                {service.groups ? (
                  service.groups.map((g) => (
                    <div key={g.title} className="mt-5">
                      {service.groups.length > 1 && (
                        <p className="text-xs font-semibold tracking-widest text-stone-400 mb-3 uppercase">
                          {L(g, "title")}
                        </p>
                      )}
                      <div className="space-y-2">
                        {g.items.map((it) => {
                          flatIdx += 1;
                          const i = flatIdx;
                          return (
                            <div
                              key={it.label}
                              className="flex items-center justify-between gap-3 rounded-2xl bg-[#FAF9F6] px-4 py-3"
                            >
                              <div className="min-w-0">
                                <p className="text-base font-medium text-stone-800 truncate">
                                  {L(it, "label")}
                                </p>
                                <p className="text-sm">
                                  <span className="line-through text-stone-400 mr-1.5">
                                    ₹{(it.price + OFFER_MARKUP.cleaning).toLocaleString("en-IN")}
                                  </span>
                                  <span className="font-bold text-[#EAB308]">₹{it.price.toLocaleString("en-IN")}</span>
                                </p>
                              </div>
                              <motion.button
                                onClick={() => bookItem(it, g)}
                                data-testid={`tier-book-${service.id}-${i}`}
                                whileTap={{ scale: 0.95 }}
                                className="rounded-full bg-[#457B9D] text-white text-sm font-semibold px-5 py-2.5 shrink-0 hover:bg-[#386682] transition-colors duration-150"
                              >
                                Book Service
                              </motion.button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="mt-5">
                    <p className="text-sm text-stone-500">{t.priceOnEnquiry} — WhatsApp.</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Reveal>
  );
};

export const CleaningSection = ({ onBook, forceOpenId, forceKey }) => {
  const { t } = useLanguage();
  const ch = t.chapters.cleaning;

  return (
    <section id="cleaning" className="py-20 sm:py-28" data-testid="cleaning-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-8">
        <Reveal className="mb-12 sm:mb-16">
          <div className="flex items-start gap-5 sm:gap-8">
            <span
              className="font-display text-7xl sm:text-8xl lg:text-9xl leading-none font-semibold text-transparent select-none"
              style={{ WebkitTextStroke: "1.5px #457B9D" }}
            >
              {ch.no}
            </span>
            <div className="max-w-xl pt-2 sm:pt-5">
              <p className="text-xs font-semibold tracking-[0.25em] text-[#457B9D] mb-3 uppercase">
                {ch.eyebrow}
              </p>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-stone-900">
                {ch.title}
              </h2>
              <p className="mt-4 text-base text-stone-500 leading-relaxed">{ch.sub}</p>
            </div>
          </div>
        </Reveal>
        <div className="space-y-5">
          {cleaningServices.map((s, i) => (
            <CleaningCard
              key={s.id}
              service={s}
              index={i}
              onBook={onBook}
              forceOpen={forceOpenId === s.id}
              forceKey={forceKey}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
